import { RegionId } from "../geo/types";
import { DEFAULT_QUIZ_CONFIG, QuizConfig } from "./QuizConfig";

export type QuizResult = {
  correct: boolean;
  targetId: RegionId;
};

export type QuizLastResult = "correct" | "incorrect" | null;

export class QuizEngine {
  private config: QuizConfig;
  private ids: RegionId[] = [];
  private weights = new Map<RegionId, number>();
  private bag: RegionId[] = [];
  private targetId: RegionId | null = null;
  private scoreCorrect = 0;
  private scoreTotal = 0;
  private lastResult: QuizLastResult = null;

  constructor(config: QuizConfig = DEFAULT_QUIZ_CONFIG) {
    this.config = config;
  }

  init(ids: RegionId[]): void {
    this.ids = [...ids];
    this.weights.clear();
    for (const id of ids) {
      this.weights.set(id, 1);
    }
    this.scoreCorrect = 0;
    this.scoreTotal = 0;
    this.lastResult = null;
    this.refillBag();
    this.targetId = this.drawFromBag();
  }

  getTargetId(): RegionId | null {
    return this.targetId;
  }

  getScore(): { correct: number; total: number } {
    return { correct: this.scoreCorrect, total: this.scoreTotal };
  }

  getLastResult(): QuizLastResult {
    return this.lastResult;
  }

  handleSelection(regionId: RegionId): QuizResult {
    if (!this.targetId) {
      throw new Error("QuizEngine is not initialized");
    }

    const correct = regionId === this.targetId;
    this.scoreTotal += 1;
    this.lastResult = correct ? "correct" : "incorrect";

    if (correct) {
      this.scoreCorrect += 1;
      this.weights.set(regionId, 1);
    } else {
      const current = this.weights.get(this.targetId) ?? 1;
      const next = Math.min(
        this.config.maxMistakeWeight,
        current + this.config.mistakeWeightIncrement,
      );
      this.weights.set(this.targetId, next);
    }

    return { correct, targetId: this.targetId };
  }

  advanceTarget(): RegionId {
    if (this.bag.length === 0) {
      this.refillBag();
    }
    const next = this.drawFromBag(this.targetId ?? undefined);
    this.targetId = next;
    return next;
  }

  private refillBag(): void {
    const nextBag: RegionId[] = [];
    for (const id of this.ids) {
      const weight = Math.max(1, Math.round(this.weights.get(id) ?? 1));
      for (let i = 0; i < weight; i += 1) {
        nextBag.push(id);
      }
    }
    this.bag = this.shuffle(nextBag);
    if (this.targetId && this.bag.length > 1 && this.bag[0] === this.targetId) {
      const swapIndex = this.bag.findIndex((id) => id !== this.targetId);
      if (swapIndex > 0) {
        [this.bag[0], this.bag[swapIndex]] = [this.bag[swapIndex], this.bag[0]];
      }
    }
  }

  private drawFromBag(avoidId?: RegionId): RegionId {
    let next = this.bag.shift();
    if (next && avoidId && next === avoidId && this.bag.length > 0) {
      const swap = this.bag.shift();
      if (swap) {
        this.bag.unshift(next);
        next = swap;
      }
    }
    if (!next) {
      this.refillBag();
      const fallback = this.bag.shift();
      if (!fallback) {
        throw new Error("QuizEngine bag is empty");
      }
      return fallback;
    }
    return next;
  }

  private shuffle(list: RegionId[]): RegionId[] {
    const array = [...list];
    for (let i = array.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }
}
